/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/ban-types */
import { Component, OnInit, Input, ContentChildren, QueryList, AfterContentInit, ViewChild, Host, Optional, OnDestroy, ElementRef,
	HostListener, AfterViewChecked, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { CdkColumnDef, CdkTable, CdkHeaderRowDef, CdkFooterRowDef } from '@angular/cdk/table';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { PcgSortDirective } from './sort/sort.directive';
import { PcgTableColumn } from './pcg-table-column.interface';
import { TableNoServerSide } from './table-no-server-side.class';
import { param } from '../http-params';
import { TableServerSide } from './table-server-side.class';
import { SecurityService } from '../../core/security/security.service';
import { ButtonFilterService } from './table-top/button-filter/button-filter.service';

import { PcgTableInput } from '../models/pcg-table-input';
import { PcgTableInputColumn } from '../models/pcg-table-input-column';
import { PcgTableResult } from '../models/pcg-table-result';

@Component({
    selector: 'pcg-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: false
})
export class TableComponent<T> implements OnInit, AfterContentInit, AfterViewChecked, OnDestroy {
	@Input() dataSource;
	@Input() columnDefs: Map<string, PcgTableColumn> = new Map<string, PcgTableColumn>();
	@Input() multipleSearch = false;
	@Input() showTableTop = true;
	@Input() showPagination = true;
	@Input() serverSide = true;
	@Input() pageSizeSource: BehaviorSubject<number>;
	@Input() pageLengths = [10, 25, 50, 100, 250, 500];
	@Input() pageSize = 100;
	@Input() searchDebounceTime = 400;
	@Input() alwaysBoxed = false; // Forces the table to be in box regardless of screen size
	@Input() responsive = true; // Whether or not to put the table in a responsive box
	@Input() fixedHeader = true; // Whether or not to have a sticky header, only available with responsive grids
	@Input() fixedFooter = true; // Whether or not to have a sticky footer, only available with responsive grids
	@Input() callbackFunc: (colResult: PcgTableResult) => void;
	@Input() ajaxData: any;
	@Input() canGlobalSearch = true;
	@Input() canExactMatchSearch = true;
	@Input() canShowHide = true;
	@Input() canShowHideColumns = true;	
	@Input() isRowClickable = false;
	// Inputs for implementing the table dropdown filter features
	/** Pass through filter id to implement table dropdown filters. */
	@Input() filterId: string = null;
	/** Pass through a filtermap to show filters being utilized. Ex. src\app\shared\business-areas\order-list\order-list */
	@Input() filterMap: {} = null;
	/** Pass through empty filter array for special reset button visibility behavior. */
	@Input() emptyFilters: {} = null;
	/** Pass through false if using the table dropdown filters and they do not have reset capabilities */
	@Input() canResetFilters = true;

	@Output() tableReceive = new EventEmitter<PcgTableResult>();

	@ContentChildren(CdkColumnDef) contentColumnDefs: QueryList<CdkColumnDef>;

	@ViewChild(CdkTable, { static: true }) table: CdkTable<T>;
	@ViewChild(CdkHeaderRowDef, { static: true }) searchHeader: CdkHeaderRowDef;
	@ViewChild(CdkFooterRowDef, { static: true }) footerRow: CdkFooterRowDef;
	@ViewChild('tableContainer', { static: true }) tableContainer: ElementRef;	

	// This object contains the table data sent to the server on the previous request
	prevTableInput: PcgTableInput = null;

	// Keep track of total records
	totalDataCount = 0;
	filteredDataCount = 0;

	// Used to set the top offest for the search row
	searchRowTopOffset = 0;
	tableNum = 0;

	// The current filter text / exact match
	filterSource = new BehaviorSubject<string>('');
	filter$ = this.filterSource.asObservable();
	exactMatchSource = new BehaviorSubject<boolean>(false);
	exactMatch$ = this.exactMatchSource.asObservable();
	perColumnSearchSource = new BehaviorSubject<string[]>([]);
	perColumnSearch$ = this.perColumnSearchSource.asObservable();

	// Pagination variables
	currentPageSource = new BehaviorSubject<number>(1);
	currentPage$ = this.currentPageSource.asObservable();
	pageSize$: Observable<number>;

	// Data variables
	data: T[];
	dataNoServerSideUpdateSource = new BehaviorSubject<T[]>([]);
	dataSource$: Observable<T[]>;
	filteredDataSource = new BehaviorSubject<T[]>([]);
	filteredData$ = this.filteredDataSource.asObservable();
	dataOnPageSource = new BehaviorSubject<any[]>([{}]);
	dataOnPage$ = this.dataOnPageSource.asObservable();

	// Keep track of our AJAX call subscriptions
	subscriptions: Subscription = new Subscription();

	// Whether or not the table is currently responsive
	isResponsive = false;

	// Whether or not the table has protected fields
	hasProtectedFields = false;

	// Whether or not the table has completed a server request
	hasCompletedServerRequest = false;

	constructor(
		@Host() @Optional() public pcgSort: PcgSortDirective,
		private sec: SecurityService,
		private http: HttpClient,
		private cdRef: ChangeDetectorRef,
		public btnFilterService: ButtonFilterService
	) {}

	// Fix responsive and search header top on resize
	@HostListener('window:resize')
	onResize() {
		this.fixResponsive();
		// I need this timeout because it takes 100 ms for the gross dynamic
		// nav sticky CSS to be added in header.component.ts :(
		setTimeout(() => { this.fixSearchHeaderTop(); }, 150);
	}

	// A few simple lambda functions that are used in the template
	hasData = () => this.filteredDataCount !== 0;
	hasNoData = () => this.filteredDataCount === 0;
	getColDefs = () => Array.from(this.columnDefs.keys());
	getVisibleColDefs = () => this.getColDefs().filter(key => this.columnDefs.get(key).isVisible !== false);
	getSearchColDefs = () => this.getColDefs().map(key => this.columnDefs.get(key).searchColumn || key);
	getColHeaderDefs = () => this.getVisibleColDefs().map(key => `${key}_search`);
	getColSearchClasses = (key: string) => this.columnDefs.get(key).multiSearchCellClasses;
	canSearch = (key: string) => this.columnDefs.get(key).canSearch !== false;
	isNdc = (key: string) => this.columnDefs.get(key).isNdc !== false;

	ngOnInit() {
		if (!this.filterId) { this.canResetFilters = false; } // Set canResetFilters is dropdown filters are not being implemented

		window['tableCount'] = window['tableCount'] ?? 1;
		this.perColumnSearchSource.next(Array.from(new Array(this.getColDefs().length), () => ''));
		if (typeof this.dataSource !== 'string') {
			this.dataSource$ = this.dataSource instanceof Observable ? this.dataSource : of(this.dataSource);
		}
		this.tableNum = window['tableCount']++;

		this.pageSizeSource = new BehaviorSubject<number>(this.pageSize);
		this.pageSize$ = this.pageSizeSource.asObservable();

		if (typeof this.dataSource === 'string') {
			// Add our server-side scripting events
			if (this.serverSide === true) {
				new TableServerSide(this).addServersideEvents();
			} else {
				this.ajaxReload(o => {
					this.data = o.data;
					new TableNoServerSide(this).addNoServersideEvents();
				});
			}
		} else {
			// Add our client-side scripting events
			new TableNoServerSide(this).addNoServersideEvents();
		}
	}

	ngAfterContentInit() {
		// Add passed in column definitions
		let hasFooter = false;
		this.contentColumnDefs.forEach(o => {
			this.table.addColumnDef(o);
			if (typeof o.footerCell !== 'undefined') { hasFooter = true; }
		});

		if (hasFooter) { this.table.addFooterRowDef(this.footerRow); }

		// Add the multiple search, if desired
		if (this.multipleSearch) { this.table.addHeaderRowDef(this.searchHeader); }
	}

	/** This is hacky, and unfortunately, I don't know a better place to put this code.
	 * We need to constantly make sure the table becomes overflow: auto / fixed height if it becomes wider
	 * than the table container. It's also necessary to make sure the offset for the second header row
	 * containing searches is correct. Both of these things can change based on all kinds of table operations,
	 * and it needs to be fixed based on what is rendered.
	 */
	ngAfterViewChecked() {
		this.fixResponsive();
		this.fixSearchHeaderTop();
	}

	// Unsubscribe from our subscription(s)
	ngOnDestroy() {
		if (this.multipleSearch) {
			const dynamicStyle = document.getElementById(`searchHeader-${this.tableNum}`);
			if (dynamicStyle) { document.head.removeChild(dynamicStyle); }
		}
		this.subscriptions.unsubscribe();
	}

	updatePerColumnSearch(index: number, newValue: string) {
		// Make shallow copy of current column search
		const newColumnSearch = this.perColumnSearchSource.value.slice(0);
		// Set new value
		newColumnSearch[index] = newValue;
		// Update the per column search values
		this.perColumnSearchSource.next(newColumnSearch);
	}

	getExcel(exportName: string = null) { this.ajaxReload(() => {}, 'Excel', exportName); }

	getPdf(exportName: string = null) { this.ajaxReload(() => {}, 'PDF', exportName); }

	ajaxReload(
		ajaxReturnFunc: (colResult: PcgTableResult) => void = () => {},
		exportType: string = null,
		reportName: string = null
	) {
		// Create a column list containig sort and per column search information
		const perColSearches = this.perColumnSearchSource.value;
		const columns: PcgTableInputColumn[] = [];
		const colNames = this.getColDefs();
		for (let i = 0; i < perColSearches.length; ++i) {
			const theCol: PcgTableInputColumn = { searchText: perColSearches[i] };
			const sortData = this.pcgSort.sortData;
			const colName = colNames[i];
			const mySort = sortData.find(o => o[0] === colName);
			if (mySort) {
				theCol.sortColumnNum = this.pcgSort.sortData.indexOf(mySort);
				theCol.sortDirection = mySort[1];
			} else {
				theCol.sortColumnNum = null;
				theCol.sortDirection = null;
			}
			columns.push(theCol);
		}

		// Default the report name to what is in the h1, if nothing provided
		const theH1 = document.querySelector('h1');
		if (theH1 && !reportName) { reportName = theH1.innerText; }

		// Create the object to send to the server
		const obj: PcgTableInput = {
			columns: null,
			start: this.pageSizeSource.value * (this.currentPageSource.value - 1),
			length: !this.showPagination ? -1 : this.pageSizeSource.value,
			searchText: this.filterSource.value,
			exactMatch: this.exactMatchSource.value,
			exportType,
			reportName,
		};

		// Check if they are changing exact match + have no search string
		if (
			this.prevTableInput !== null &&
			this.prevTableInput.exactMatch !== obj.exactMatch &&
			obj.searchText === ''
		) {
			this.prevTableInput = obj;
			return; // Don't do a request if they are checking/unchecking exact match with no search string
		}
		// Remember the previous table input data
		this.prevTableInput = obj;

		// Get a query string based off of the user supplied ajax data
		const userAjaxDataString = !this.ajaxData
			? ''
			: typeof this.ajaxData === 'string'
			? this.ajaxData
			: param(this.ajaxData);

		// Cancel any current AJAX calls to the server
		this.subscriptions.unsubscribe();
		this.subscriptions = new Subscription();

		// User must provide an Excel password if we have protected fields
		if (exportType === 'Excel' && this.hasProtectedFields) {
			this.sec.promptPassword(excelPassword => {
				this.sendServerRequest(obj, userAjaxDataString, excelPassword, columns, ajaxReturnFunc);
			});
			return;
		}

		this.hasCompletedServerRequest = exportType === 'Excel' || exportType === 'PDF';
		this.sendServerRequest(obj, userAjaxDataString, '', columns, ajaxReturnFunc);
		return true;
	}

	/** Show or hide a column in the table. This is equivalent to
	 * clicking the "Show / Hide" button and toggling the visibility
	 * of a column.
	 */
	hideShowColumn(columnName: string, isVisible: boolean) { this.columnDefs.get(columnName).isVisible = isVisible; }

	/**
	 * Send the table information to the server.
	 * Table data will be updated when it returns.
	 */
	private sendServerRequest(
		obj: PcgTableInput,
		userAjaxDataString: string,
		excelPassword: string,
		columns: PcgTableInputColumn[],
		ajaxReturnFunc: (colResult: PcgTableResult) => void
	) {
		this.subscriptions.add(
			this.http
				.get(
					`${this.dataSource}?` +
						`${param(obj, true)}&${userAjaxDataString}&excelPassword=${encodeURIComponent(excelPassword)}` +
						`&columnJson=${encodeURIComponent(
							JSON.stringify(
								columns.map(o => {
									const newObj: any = {};
									if (o.searchText !== null && o.searchText !== '') { newObj.searchText = o.searchText; }
									if (o.sortColumnNum !== null) { newObj.sortColumnNum = o.sortColumnNum; }
									if (o.sortDirection !== null) { newObj.sortDirection = o.sortDirection; }
									return newObj;
								})
							)
						)}`
				).subscribe((tableResult: PcgTableResult) => {
					if (tableResult.exportLocation) {
						window.open(tableResult.exportLocation);
					} else {
						this.totalDataCount = tableResult.recordsTotal;

						// Need to apply filters if we are handling data
						if (this.serverSide === false) {
							this.dataNoServerSideUpdateSource.next(tableResult.data);
						} else {
							// Otherwise, set the next page and counts from server
							this.filteredDataCount = tableResult.recordsFiltered;
							this.data = this.filteredDataCount === 0 ? [{}] : tableResult.data;
							this.dataOnPageSource.next(this.data);
							this.hasCompletedServerRequest = true;
						}

						if (tableResult.hasProtectedFields) { this.hasProtectedFields = true; }
						this.tableReceive.emit(tableResult);
						ajaxReturnFunc(tableResult);
						if (this.callbackFunc) { this.callbackFunc(tableResult); }
					}
				})
		);
	}

	/** This fixes the sticky offset for the search columns, when
	 * multiple search is enabled
	 */
	private fixSearchHeaderTop() {
		if (this.multipleSearch) {
			const tableHeadRow = this.tableContainer.nativeElement.querySelector('table thead tr th');
			const computedStyles = getComputedStyle(tableHeadRow);
			const newSearchRowTopOffset = parseInt(computedStyles.top, 10) + parseInt(computedStyles.height, 10) - 1;

			if (newSearchRowTopOffset !== this.searchRowTopOffset) {
				this.searchRowTopOffset = newSearchRowTopOffset;
				this.setDynamicStyle(
					`searchHeader-${this.tableNum}`,
					`
					.pcg-table-fixed-header .pcg-table-${this.tableNum} thead tr.search-row th,
					.pcg-table-fixed-header .pcg-table-${this.tableNum} thead tr.search-row {
						top: ${newSearchRowTopOffset}px
					}`
				);
				this.cdRef.detectChanges();
			}
		}
	}

	private setDynamicStyle(name: string, styles: string) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { document.head.removeChild(dynamicStyle); }
		dynamicStyle = document.createElement('style');
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}

	/** Make the table container overflow auto and fixed height
	 *  based on whether or not the table is overflowing its container.
	 */
	private fixResponsive() {
		if (this.alwaysBoxed && !this.isResponsive) {
			this.isResponsive = true;
			this.cdRef.detectChanges();
		} else if (this.responsive) {
			const tableContainerWidth = this.tableContainer.nativeElement.getBoundingClientRect().width;
			const tableWidth = this.tableContainer.nativeElement.querySelector('table').getBoundingClientRect().width;
			if (this.isResponsive !== tableWidth > tableContainerWidth) {
				this.isResponsive = tableWidth > tableContainerWidth;
				this.cdRef.detectChanges();
			}
		}
	}
}
